import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * The dashboard's button.
 *
 * `primary` is brand teal — the single most important action on a screen.
 * `green` is the muted UI green, for actions that confirm a selection you've
 * already made ("Save", "Apply") rather than starting something new. If a
 * screen has two teal buttons, one of them is wrong.
 */

type Variant = 'primary' | 'green' | 'secondary' | 'ghost' | 'danger' | 'dark';
type Size = 'sm' | 'md' | 'lg';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-flup-brand text-white hover:bg-flup-brand-press',
  green: 'bg-flup-green text-white hover:bg-flup-green-press',
  secondary: 'bg-flup-surface text-flup-ink border border-flup-hairline-2 hover:bg-flup-surface-alt',
  ghost: 'bg-transparent text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink',
  danger: 'bg-flup-danger-soft text-flup-danger hover:bg-[#FBDCDC]',
  dark: 'bg-flup-nav text-white hover:bg-flup-nav-press',
};

const SIZES: Record<Size, { box: string; text: 'caption' | 'labelSemi' | 'bodySemi' }> = {
  sm: { box: 'h-8 px-3 gap-1.5 rounded-flup-xs', text: 'caption' },
  md: { box: 'h-10 px-4 gap-2 rounded-flup-sm', text: 'labelSemi' },
  lg: { box: 'h-11 px-5 gap-2 rounded-flup-sm', text: 'bodySemi' },
};

export interface FlupButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
  loading?: boolean;
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, FlupButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      icon,
      trailingIcon,
      loading = false,
      fullWidth = false,
      disabled,
      className,
      type = 'button',
      children,
      ...rest
    },
    ref,
  ) => {
    const s = SIZES[size] ?? SIZES.md;
    const inert = disabled || loading;

    return (
      <button
        ref={ref}
        type={type}
        disabled={inert}
        aria-busy={loading || undefined}
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap select-none',
          'transition-colors duration-150 active:scale-[0.99]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand focus-visible:ring-offset-2',
          '[&_svg]:shrink-0',
          s.box,
          VARIANTS[variant] ?? VARIANTS.primary,
          fullWidth && 'w-full',
          inert && 'opacity-50 pointer-events-none',
          className,
        )}
        {...rest}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> : icon}
        {children ? (
          <Text variant={s.text} tone="inherit" as="span">
            {children}
          </Text>
        ) : null}
        {!loading ? trailingIcon : null}
      </button>
    );
  },
);

Button.displayName = 'FlupButton';

/** Square icon-only control — the print/delete affordances on a detail panel. */
export const IconButton = React.forwardRef<
  HTMLButtonElement,
  Omit<FlupButtonProps, 'children' | 'icon'> & { label: string; children: React.ReactNode }
>(({ variant = 'secondary', size = 'md', label, className, children, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={label}
    title={label}
    className={cn(
      'inline-grid place-items-center shrink-0 transition-colors duration-150',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flup-brand focus-visible:ring-offset-2',
      'disabled:opacity-50 disabled:pointer-events-none',
      size === 'sm' ? 'w-8 h-8 rounded-flup-xs' : 'w-10 h-10 rounded-flup-sm',
      VARIANTS[variant] ?? VARIANTS.secondary,
      className,
    )}
    {...rest}
  >
    {children}
  </button>
));

IconButton.displayName = 'FlupIconButton';

export default React.memo(Button);
